import { useDebouncedValue, useLocalStorage } from "@mantine/hooks"
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleHalf,
  DotsThreeVertical,
  List,
  MagnifyingGlass,
  Plus,
  Prohibit,
  Receipt,
  Timer,
  Truck,
  Warning,
  WarningCircle,
} from "@phosphor-icons/react"
import { openConfirmDialog } from "@vesatogo/grass-confirm-modal"
import {
  Avatar,
  Button,
  Checkbox,
  CodenameTag,
  InputGroup,
  Menu,
  MenuItem,
  Note,
  OverflowMenu,
  Popover,
  SegmentedControl,
  Tag,
  Toast,
  Tooltip,
} from "@vesatogo/grass-core"
import { Cell, TableView } from "@vesatogo/grass-table"
import { inr } from "@vesatogo/utils"
import dayjs from "dayjs"
import { merge, pick } from "lodash-es"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import {
  Link,
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom"
import { UseQueryArgs, useClient } from "urql"
import { useAuthorization } from "~/components/AuthorizationProvider"
// import { openDeleteTradesConfirm } from "~/components/DeleteTradesConfirm"
import GenerateInvoices from "~/components/GenerateInvoices"
import { IssuanceNoteModal } from "~/components/IssuanceNoteModal"
import { renderMultiCommoditySubRow } from "~/components/MultiCommoditySubRow"
import TradeActions from "~/components/TradeActions"
import TradeKindTag from "~/components/TradeKindTag"
import Codenames from "~/constants/Codenames"
import FilterTypes from "~/constants/FilterTypes"
import {
  TradeKinds,
  canAddAdvancePayment,
  hasPendingAdvanceApproval,
} from "~/constants/Kinds"
import { Permission } from "~/constants/Permissions"
import { siteConfig } from "~/constants/config"
import { OrdersCustom } from "~/constants/icons"
import { APIRoutes, AppRoutes } from "~/constants/routes"
import {
  AllTradeRequestsIdsQuery,
  AllTradeRequestsQueryVariables,
  Order_By,
  Trade_Bool_Exp,
  UsersQuery,
  useAllTradeIssuanceNotesQuery,
  useAllTradeRequestsQuery,
  useAllTradeRequestsStatusSubscription,
  useAuthenticatedUserQuery,
  useBookingTradeSummaryQuery,
  useBulkUpdateTradeMutation,
  useTradeAdvancePendingStatusQuery,
  useTradeHashesQuery,
} from "~/generated/graphql"
import { ALL_TRADE_REQUESTS_IDS } from "~/graphql/trade.gql"
import { usePayableSelector } from "~/pages/d/payable/advance/PayableSelector"
import TradeRequestsFilter from "~/pages/d/trade-requests/TradeRequestsFilter"
import UpdateTradeStatus from "~/pages/d/trade-requests/UpdateTradeStatus"
import { useTradeSelectorList } from "~/store/trade-selector.store"
import { getConsolidatedBookingQuantity } from "~/utils/bookingPavtiUtils"
import { downloadPdf } from "~/utils/downloadPdf"
// import { useDumpTrades } from "~/utils/useDumpTrades"

const COMMON_FIELDS = [
  "id",
  "current_status_id",
  "buyer",
  "seller",
  "standard_deduction",
  "total_amount",
  "total_quantity",
  "expense_amount",
  "item_units",
  "trade_items",
  "trade_items_aggregate",
  "commodity",
  "created_at",
  "kind",
  "trip",
  "department",
  "buyer_hash",
  "seller_hash",
]

export type FilterType = {
  sb_seller?: UsersQuery["base_user"][0]
  sb_buyer?: UsersQuery["base_user"][0]
  com_commodity?: any
  com_variety?: any
  com_is_multiple?: any
  oth_mode?: any
  oth_kind?: any
  oth_status?: any
  oth_facilitator?: any
  oth_created_at?: any
}

const TradeMobileCard = ({ original, columns }: any) => {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex flex-col gap-3 mb-3">
      <div className="flex justify-between items-start pb-2 border-b border-gray-100">
        <div className="flex items-start gap-2 max-w-[60%]">
          <div className="pt-1">
            {columns.find((c: any) => c.id === "selection")?.Cell &&
              (() => {
                const CellComp = columns.find(
                  (c: any) => c.id === "selection"
                ).Cell
                return <CellComp value={original.id} row={{ original }} />
              })()}
          </div>
          <div className="flex flex-col items-start gap-1">
            {columns.find((c: any) => c.accessor === "name")?.Cell &&
              (() => {
                const CellComp = columns.find(
                  (c: any) => c.accessor === "name"
                ).Cell
                return <CellComp value={original.name} row={{ original }} />
              })()}
            <div className="mt-1">
              {columns.find((c: any) => c.accessor === "kind")?.Cell &&
                (() => {
                  const CellComp = columns.find(
                    (c: any) => c.accessor === "kind"
                  ).Cell
                  return <CellComp value={original.kind} row={{ original }} />
                })()}
            </div>
          </div>
        </div>
        {/* <div className="flex flex-col items-end gap-1 max-w-[40%]">
          {columns.find((c: any) => c.accessor === "current_status_id")?.Cell &&
            (() => {
              const CellComp = columns.find(
                (c: any) => c.accessor === "current_status_id"
              ).Cell
              return (
                <CellComp
                  value={original.current_status_id}
                  row={{ original }}
                />
              )
            })()}
        </div> */}
      </div>

      <div className="grid gap-y-4 gap-x-2">
        {columns.map((col: any, idx: number) => {
          if (
            [
              "selection",
              "name",
              "kind",
              "current_status_id",
              "updated_at",
            ].includes(col.id || col.accessor)
          )
            return null
          if (col.Header === "") return null

          let val = original
          if (col.accessor) {
            val = col.accessor
              .replace(/\[(\w+)\]/g, ".$1")
              .split(".")
              .reduce((o: any, i: string) => o?.[i], original)
          }
          const CellComp = col.Cell
          return (
            <div key={idx} className="grid grid-cols-2  gap-1 overflow-hidden">
              <span className="text-[11px] text-gray-500 uppercase tracking-wider whitespace-nowrap overflow-hidden text-ellipsis">
                {typeof col.Header === "string" ? col.Header : "Select"}
              </span>
              <div className="text-sm font-500 overflow-hidden text-ellipsis [&>div]:!items-start [&>div]:!text-left">
                {CellComp ? (
                  <CellComp value={val} row={{ original }} />
                ) : (
                  val?.toString() || "--"
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex justify-end items-center gap-3 pt-2 border-t border-gray-100 mt-1">
        {columns
          .filter((c: any) => c.accessor === "updated_at")
          .map((col: any, idx: number) => {
            let val = original
            if (col.accessor) {
              val = col.accessor
                .replace(/\[(\w+)\]/g, ".$1")
                .split(".")
                .reduce((o: any, i: string) => o?.[i], original)
            }
            const CellComp = col.Cell
            return <CellComp key={idx} value={val} row={{ original }} />
          })}
      </div>
    </div>
  )
}

const shareViaWhatsApp = async ({ hash }) => {
  const id = hash
  const url = `https://${
    import.meta.env.VITE_REST_URL
  }/${APIRoutes.whatsAppTradeReceipt(id)}`

  const token = localStorage.getItem("token")

  const response = await fetch(url, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  })
  if (!response.ok)
    toast.custom(
      <Toast title={"Unable to send to WhatsApp!"} intent="danger" />
    )
  else toast.custom(<Toast title="Message will be sent!" />)
}

const AllTradeRequests = () => {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const [{ data: aData }] = useAuthenticatedUserQuery()

  const search = params.get("search")
  const updatedAt = params.get("updatedAt")
  const page = Number(params.get("page")) || 1
  const [filter, setFilter] = useState<FilterType>(() => {
    const savedFilter = localStorage.getItem(FilterTypes.TradeFilter)
    return savedFilter ? JSON.parse(savedFilter) : {}
  })

  useEffect(() => {
    localStorage.setItem(FilterTypes.TradeFilter, JSON.stringify(filter))
  }, [filter])

  const [debouncedSearch] = useDebouncedValue(
    search?.trim(),
    siteConfig.debounce
  )
  const [isMulti, setMulti] = useLocalStorage<boolean>({
    key: "tradeIsMultiCommodity",
    defaultValue: false,
    getInitialValueInEffect: false,
  })
  const canViewInvoice = useAuthorization(Permission.ViewInvoice)
  const canViewTrades = useAuthorization(Permission.ViewTrade)
  const canVerifyTrades = useAuthorization(Permission.CanVerifyTrades)
  const canDeleteTrades = useAuthorization(Permission.CanDeleteTrades)
  const canSelectAllPages = useAuthorization(Permission.canSelectAllTradePages)
  const buyerCode = filter.sb_buyer?.codename
  const sellerCode = filter.sb_seller?.codename
  const commodityCode = filter.com_commodity?.codename
  const varietyCode = filter.com_variety?.codename
  const facilitatorCode = filter.oth_facilitator?.codename
  const currentDeptCodename = aData?.me?.agent?.current_department?.codename
  const [department] = useLocalStorage({ key: "department" })
  const [viewPackaging] = useLocalStorage({ key: "viewPackaging" })
  const isPackaging = viewPackaging === "2"
  const client = useClient()

  const canShareViaWhatsApp = useAuthorization(
    Permission.canSendTradeReceiptsViaWhatsApp
  )
  const canAddMultiCommodity = useAuthorization(
    Permission.CanAddMultiCommodityTradesAndReceipts
  )

  const createdAt = filter?.oth_created_at
  const whereClause: Trade_Bool_Exp = {
    ...(debouncedSearch
      ? Number(debouncedSearch)
        ? { id: { _eq: Number(debouncedSearch) } }
        : {
            _or: [
              { tracking_code: { _ilike: `%${debouncedSearch}%` } },
              {
                vehicle: {
                  name: {
                    _ilike: `%${debouncedSearch}%`,
                  },
                },
              },
            ],
          }
      : {}),
    kind: { _eq: filter.oth_kind },
    is_multiple_commodity: {
      _eq: isMulti,
    },
    material_kind: {
      _eq: isPackaging ? "packaging" : "commodity",
    },
    created_at: createdAt
      ? {
          _lte: dayjs(createdAt).endOf("day").toISOString(),
          _gte: dayjs(createdAt).startOf("day").toISOString(),
        }
      : undefined,
    department: {
      reference_id: {
        _eq: (department && String(department)) || currentDeptCodename,
      },
    },
    buyer: buyerCode
      ? {
          reference_id: {
            _eq: buyerCode,
          },
        }
      : undefined,
    seller: sellerCode
      ? {
          reference_id: {
            _eq: sellerCode,
          },
        }
      : undefined,
    commodity: commodityCode && {
      reference_id: {
        _eq: commodityCode,
      },
    },
    commodity_variety: varietyCode && {
      reference_id: {
        _eq: varietyCode,
      },
    },
    mode: {
      _eq: filter.oth_mode,
    },
    facilitator: facilitatorCode && {
      reference_id: {
        _eq: facilitatorCode,
      },
    },
    // status: {
    //   _eq: filter.oth_status?.id,
    // },
    current_status_id: {
      _eq: filter.oth_status?.id,
    },
  }

  const tradeVariables: Omit<
    UseQueryArgs<AllTradeRequestsQueryVariables>,
    "query"
  > = {
    variables: {
      limit: siteConfig.pageSize,
      offset: (page - 1) * siteConfig.pageSize,
      order_by: { created_at: Order_By.DescNullsLast },
      where: whereClause,
    },
    pause: !department && !currentDeptCodename,
    requestPolicy: "network-only",
  }

  useEffect(() => {
    if (!canViewTrades && currentDeptCodename) {
      navigate(AppRoutes.tradeReceipts)
    }
  }, [canViewTrades, currentDeptCodename])
  const [{ data: qData, fetching }, refetch] =
    useAllTradeRequestsQuery(tradeVariables)

  const [{ data: sData }] =
    useAllTradeRequestsStatusSubscription(tradeVariables)

  const data = useMemo(
    () => (qData?.trade?.length ? merge(qData, sData) : undefined),
    [qData, sData]
  )

  const { selected, selectAll, isSelectAll, reset, setSelected } =
    useTradeSelectorList()
  const COLUMNS = useMemo(() => {
    return [
      {
        accessor: "id",
        Header({ data: tableData }) {
          const selectable = tableData?.filter(d => d.id)
          const isOk = Object.values(selected)?.filter(d => d.id)
          return (
            <div className="flex gap-2">
              <Checkbox
                labelClassName="!mb-4"
                checked={isSelectAll?.(isOk, selectable)}
                onChange={() => {
                  if (isSelectAll?.(isOk, selectable)) {
                    reset()
                  } else {
                    selectAll(
                      selectable?.map(data => pick(data, COMMON_FIELDS))
                    )
                  }
                }}
              />
              <Popover
                minimal
                // arrow={false}
                className="p-0"
                trigger="click"
                interactive
                animation="fade"
                // theme="light-border"
                placement="bottom"
                content={
                  <Menu className="max-h-52 font-600 text-gray-600">
                    {canSelectAllPages ? (
                      <MenuItem
                        onClick={async () => {
                          const { data } = await client
                            .query<
                              AllTradeRequestsIdsQuery,
                              AllTradeRequestsQueryVariables
                            >(ALL_TRADE_REQUESTS_IDS, {
                              order_by: { created_at: Order_By.DescNullsLast },
                              where: whereClause,
                            })
                            .toPromise()
                          selectAll(
                            data?.trade?.map(data =>
                              pick(data, COMMON_FIELDS)
                            ) as any
                          )
                        }}
                      >
                        Select all pages
                      </MenuItem>
                    ) : null}
                    <MenuItem
                      onClick={() => {
                        selectAll(
                          selectable?.map(data => pick(data, COMMON_FIELDS))
                        )
                      }}
                    >
                      Select this page
                    </MenuItem>
                    <MenuItem
                      onClick={async () => {
                        reset()
                      }}
                    >
                      Select none
                    </MenuItem>
                  </Menu>
                }
              >
                <CaretDown weight="fill" size={18} className="cursor-pointer" />
              </Popover>
            </div>
          )
        },
        Cell({ value, row }) {
          const { selected, setSelected } = useTradeSelectorList()
          if (!row?.original?.id) return null

          return (
            <Checkbox
              checked={selected[value] ? true : false}
              onChange={() => {
                setSelected(pick(row.original, COMMON_FIELDS))
              }}
            />
          )
        },
      },
      {
        id: "expander",
        Header: "",
        Cell: ({ row }: any) =>
          row?.original?.is_multiple_commodity ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: { paddingLeft: `${row.depth * 1.5}rem` },
              })}
            >
              {row.isExpanded ? (
                <CaretDown size={16} />
              ) : (
                <CaretRight size={16} />
              )}
            </span>
          ) : null,
      },
      {
        Header: <div className="pl-3">Pavti No</div>,
        accessor: "name",
        Cell({ row: { original } }) {
          if (!original.id) return "--"
          return (
            <div className="pl-4">
              <Link
                className="link-text text-left"
                to={`/d/trade-requests/${original.id}`}
              >
                Pavti #{original.id}
              </Link>
            </div>
          )
        },
      },
      {
        Header: "Pavti Type",
        accessor: "kind",
        Cell({ value }) {
          // const kind = TRADE_KINDS.find(k => k.id === value)
          // if (!kind?.name) return "--"

          return <TradeKindTag kind={value as any} />
        },
      },
      {
        Header: "Trip Id",
        accessor: "trip.id",
        className: "w-[5%]",
        Cell({ value }) {
          if (!value) return "--"

          return <Cell title={`Trip #${value}`} />
        },
      },
      {
        Header: "Created On",
        accessor: "created_at",
        Cell({ value }) {
          if (!value) return "--"
          return (
            <Cell
              title={dayjs(value).format("D MMM 'YY")}
              subtitle={dayjs(value).format("hh:mm a")}
            />
          )
        },
      },
      {
        Header: "Supplier",
        accessor: "seller.user[0]",
        Cell({ value, row: { original } }) {
          if (!value) return "--"
          const credit = original?.credit?.[0]?.id
          const creditVerified = original?.credit?.[0]?.is_verified
          const creditRejected = original?.credit?.[0]?.is_verified === false
          const debit = original?.debit?.[0]?.id
          const debitVerified = original?.debit?.[0]?.is_verified
          const debitRejected = original?.debit?.[0]?.is_verified === false
          return (
            <Cell
              title={
                <div className="flex items-center gap-1">
                  {value.full_name}{" "}
                  {value?.is_blocked && (
                    <Prohibit color="red" size={20} weight="fill" />
                  )}
                  {credit ? (
                    <Tag
                      intent={
                        creditVerified
                          ? "primary"
                          : creditRejected
                          ? "danger"
                          : "warning"
                      }
                      className="!rounded-xl"
                      variant="outline"
                    >
                      Credit
                    </Tag>
                  ) : null}
                  {debit ? (
                    <Tag
                      intent={
                        debitVerified
                          ? "primary"
                          : debitRejected
                          ? "danger"
                          : "warning"
                      }
                      className="!rounded-xl"
                      variant="outline"
                    >
                      Debit
                    </Tag>
                  ) : null}
                </div>
              }
              subtitle={
                <>
                  <CodenameTag referenceId={value.external_reference_id} />
                </>
              }
            />
          )
        },
      },
      {
        Header: "Buyer",
        accessor: "buyer.user[0]",
        Cell({ value }) {
          if (!value) return "--"
          return (
            <Cell
              title={
                <div className="flex items-center gap-1">
                  {value.full_name}{" "}
                  {value?.is_blocked && (
                    <Prohibit color="red" size={20} weight="fill" />
                  )}
                </div>
              }
              subtitle={
                <CodenameTag referenceId={value.external_reference_id} />
              }
            />
          )
        },
      },
      {
        Header: "Commodity",
        accessor: "trade_items",
        Cell({ value, row: { original } }) {
          if (!value) return "--"
          const items = original?.trade_items || []
          const isMultiple = original?.is_multiple_commodity
          const uniqueVarieties = new Set(
            items.map(
              (it: any) =>
                it?.commodity_variety?.id ?? it?.commodity_variety?.name
            )
          )
          const varietyCount = uniqueVarieties.size
          if (isMultiple) {
            return (
              <Cell
                title={
                  <Tag intent="info" className="!rounded-xl">
                    {varietyCount}
                  </Tag>
                }
              />
            )
          }
          return (
            <Cell
              title={items?.[0]?.commodity?.name}
              subtitle={items?.[0]?.commodity_variety?.name}
            />
          )
        },
      },
      {
        Header: "Quantity",
        accessor: "trade_items_aggregate.aggregate.sum.quantity",
        Cell({ value, row: { original } }) {
          if (!value) return "--"
          const total_returned =
            original?.trade_items_aggregate.aggregate.sum.returned_quantity
          return (
            <Cell
              title={value || "0"}
              subtitle={
                total_returned ? (
                  <Tag
                    className="!rounded-xl"
                    intent="danger"
                    variant="minimal"
                  >{`Revised: ${total_returned}`}</Tag>
                ) : (
                  ""
                )
              }
            />
          )
        },
      },
      {
        Header: "Remaining Quantity",
        accessor: "deducted_quantity_remaining",
        id: "remaining_quantity",
        Cell({ value, row: { original } }) {
          if (original?.kind === TradeKinds.HishobPatti || !value) {
            return "--"
          }
          return <Cell title={value} />
        },
      },
      {
        Header: "Rate",
        accessor: "item_units",
        Cell({ value, row: { original } }) {
          // if (!value) return "--"
          const units = original?.trade_items?.map(tr => {
            return {
              id: tr?.id,
              unit: { ...tr?.rate_unit },
              rate: tr?.rate,
              name: `${inr(tr?.rate)}/ ${tr?.rate_unit?.name}`,
            }
          })
          return original?.is_multiple_commodity ? (
            <Cell
              title={
                <OverflowMenu
                  getVisibleProps={() => {
                    return {
                      className: "!border-none !text-gray-800",
                    }
                  }}
                  visibleItems={1}
                  items={units}
                />
              }
            />
          ) : (
            <Cell
              title={`${inr(
                original.trade_items_aggregate?.aggregate?.avg.rate
              )} / ${value?.[0]?.rate_unit?.name || "na"}`}
              subtitle={inr(original.total_amount)}
            />
          )
        },
      },
      {
        Header: "Deviation Amt",
        accessor: "over_draft.amount",
        Cell({ value, row: { original } }) {
          if (!value) return "--"
          return <Cell title={`${inr(value || 0)} `} />
        },
      },
      {
        Header: "Receipt",
        accessor: "receipt_id",
        Cell({ value }) {
          if (!value) return "--"
          return (
            <Link
              className="link-text text-left"
              to={AppRoutes.tradeReceiptDetail(value)}
            >
              Receipt #{value}
            </Link>
          )
        },
      },
      {
        Header: "invoice",
        accessor: "invoice",
        Cell({ value }) {
          if (!value) return "--"
          return (
            <Cell title={`#${value?.id}`} subtitle={value?.current_status_id} />
          )
        },
      },
      {
        Header: "Placed By",
        accessor: "created_by[0]",
        Cell({ value, row }) {
          if (!value) return "--"
          return (
            <Tooltip
              content={
                <div>
                  <p>{value?.full_name || "na"}</p>
                  <p className="text-gray-400">
                    {dayjs(row?.original?.created_at).format(
                      "DD MMM 'YY | h:mm a"
                    )}
                  </p>
                </div>
              }
            >
              <Avatar
                className="relative text-center"
                name={value?.full_name}
                src={value?.photo?.url}
              />
            </Tooltip>
          )
        },
      },
      {
        Header: "Verified By",
        accessor: "action_logs.0.created_by",
        Cell({ value, row }) {
          if (!value) return "--"
          return (
            <Tooltip
              content={
                <div>
                  <p>{value?.full_name || "na"}</p>
                </div>
              }
            >
              <Avatar
                className="relative text-center"
                name={value?.full_name}
              />
            </Tooltip>
          )
        },
      },

      {
        Header: "Status",
        accessor: "current_status_id",
        Cell({ row: { original }, value }) {
          let remainingQuantity = 0
          {
            if (original?.kind === TradeKinds.Booking) {
              const [{ data: bData }] = useBookingTradeSummaryQuery({
                variables: {
                  id: original.id,
                },
                pause: original?.kind !== TradeKinds.Booking,
                requestPolicy: "network-only",
              })

              remainingQuantity = getConsolidatedBookingQuantity(bData)
            }
          }

          return (
            <Cell
              title={
                value ? (
                  <span className="flex gap-1 items-center">
                    {value}{" "}
                    {original?.status_processing && (
                      <Tooltip content="Status change in progress">
                        <Timer
                          size={16}
                          className="text-blue-300"
                          weight="fill"
                        />
                      </Tooltip>
                    )}
                    {original?.kind === TradeKinds.Booking &&
                      (remainingQuantity > 0 &&
                      remainingQuantity <
                        original?.trade_items_aggregate.aggregate.sum
                          .quantity ? (
                        <CircleHalf
                          size={16}
                          weight="fill"
                          className="text-blue-300"
                        />
                      ) : remainingQuantity === 0 ? (
                        <CheckCircle
                          size={16}
                          weight="fill"
                          className="text-green-300"
                        />
                      ) : (
                        <></>
                      ))}
                  </span>
                ) : (
                  "na"
                )
              }
              subtitle={dayjs(original.updated_at).format(
                "D MMM 'YY | hh:mm A"
              )}
            />
          )
        },
      },

      {
        Header: "",
        accessor: "updated_at",
        Cell({ row: { original } }) {
          const [params, setParams] = useSearchParams()
          const navigate = useNavigate()
          const location = useLocation()
          const [isShown, setShown] = useState(false)
          // const dumpTrades = useDumpTrades()
          const setPayableSelected = usePayableSelector(s => s.setSelected)
          const [{ data }] = useTradeHashesQuery({
            variables: {
              id: original.id,
            },
            pause: !isShown,
          })

          const [{ data: bData }] = useBookingTradeSummaryQuery({
            variables: {
              id: original.id,
            },
            pause: original?.kind !== TradeKinds.Booking,
            requestPolicy: "network-only",
          })

          const remainingQuantity =
            original?.kind === TradeKinds.Booking
              ? getConsolidatedBookingQuantity(bData)
              : undefined

          function handleOpenPdf(key: "seller_hash" | "buyer_hash") {
            return async () => {
              let token = localStorage.getItem("token")
              downloadPdf(
                APIRoutes.downloadPavti(data?.trade_by_pk?.[key] as string),
                token
              ).catch(err => {
                return toast.custom(
                  <Toast
                    title={"Unable to download the receipt"}
                    intent="danger"
                  />
                )
              })
            }
          }

          const [{ data: tradeIssuanceNotesData }] =
            useAllTradeIssuanceNotesQuery({
              variables: {
                tradeId: original.id,
              },
              pause: !isShown,
            })

          const [{ data: advanceStatusData }] =
            useTradeAdvancePendingStatusQuery({
              variables: {
                ids: [original.id],
              },
              pause: !isShown || !canAddAdvancePayment(original?.kind),
              requestPolicy: "network-only",
            })

          const hasPendingAdvance = advanceStatusData?.trade?.some(trade =>
            hasPendingAdvanceApproval(trade)
          )

          const showAdvancePendingError = () => {
            openConfirmDialog({
              isOpen: true,
              bodyClassName: "!min-w-[35rem] w-max",
              title: `Pavti #${original.id}`,
              message: (
                <>
                  <div className="flex flex-col items-center justify-center gap-5 mb-3">
                    <div className="p-5 rounded-full border-1 border-red-200 bg-red-100">
                      <Warning className="text-red" size={28} />
                    </div>
                    <span className="text-neutral-600 font-700 text-2xl">
                      Advance Approval Pending
                    </span>
                  </div>
                  <p className="text-gray-800 text-sm px-20 mb-10">
                    Child Patti cannot be created until the Sauda/Transit Patti
                    advance request is Approved or Rejected.
                  </p>
                  <Note
                    className="text-left text-sm text-neutral-500 !rounded-xl border-1 border-red-200 p-2"
                    intent="danger"
                    title={
                      (
                        <div className="flex gap-3 items-center">
                          <WarningCircle
                            className="text-red self-start"
                            size={20}
                          />
                          The advance request for this Sauda/Transit Patti is
                          still in approval pending. Please wait for it to be
                          Approved or Rejected to proceed.
                        </div>
                      ) as any
                    }
                  />
                </>
              ),
              confirmationButtonText: "View advance request",
              primaryButtonProps: { intent: "primary" },
              onSubmit: () => {
                setPayableSelected("trade")
                return navigate(
                  `${AppRoutes.advancePayable}?${createSearchParams({
                    search: String(original.id),
                  })}`
                )
              },
            })
          }

          const canUpdateStatus =
            original?.current_status_id === "Draft" ? canVerifyTrades : true

          const isDraft = original?.current_status_id === Codenames.Draft

          // const dumpTrade = async () => {
          //   const isDeleted = await dumpTrades([original.id])
          //   if (!isDeleted) return
          //   setShown(false)
          //   setTimeout(() => {
          //     refetch({ requestPolicy: "network-only" })
          //   }, 1000)
          // }

          return (
            <Popover
              onShow={() => setShown(true)}
              minimal
              arrow={false}
              className="p-0 max-h-[300px] overflow-y-scroll scrollbar-hide scrollbar-hover-show"
              trigger="click"
              interactive
              animation="fade"
              theme="light-border"
              placement="bottom"
              content={
                <Menu className="max-h-fit">
                  <MenuItem
                    onClick={() => {
                      params.set("timeline", original.id)
                      setParams(params)
                    }}
                  >
                    Timeline
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate(`/d/trade-requests/${original.id}`)
                    }}
                  >
                    View Details
                  </MenuItem>
                  <MenuItem
                    disabled={!canUpdateStatus}
                    onClick={() => {
                      params.set("update", original.id)
                      navigate(
                        {
                          pathname: location.pathname,
                          search: `?${createSearchParams(params)}`,
                        },
                        {
                          state: {
                            current_status_id: original.current_status_id,
                            status_processing: original.status_processing,
                          },
                        }
                      )
                    }}
                  >
                    Update Status
                  </MenuItem>
                  <hr />
                  <div className="text-gray-800 font-600 p-2 underline">
                    Download
                  </div>
                  <MenuItem onClick={handleOpenPdf("seller_hash")}>
                    Download Supplier Receipt
                  </MenuItem>
                  <MenuItem onClick={handleOpenPdf("buyer_hash")}>
                    Download Buyer Receipt
                  </MenuItem>
                  <hr />
                  <div className="text-gray-800 font-600 p-2 underline">
                    Add Patti
                  </div>
                  <MenuItem
                    disabled={
                      // Can add hishob patti if
                      // 1. Trade is already not a hishob patti
                      // 2. Trade status is verified
                      // 3. If Inward Patti, status is not Generate Inward
                      // 4. Remaining quantity is not zero for booking patti

                      original.advance_po &&
                      original?.kind === TradeKinds.SaudaPavti
                        ? true
                        : original?.kind === TradeKinds.HishobPatti ||
                          (original?.kind === TradeKinds.InwardPatti &&
                            original?.current_status_id !==
                              Codenames.GenerateInward &&
                            original?.current_status_id !==
                              Codenames.InwardConfirm) ||
                          (original?.kind !== TradeKinds.InwardPatti &&
                            original?.current_status_id !== "Verified") ||
                          (original?.kind === TradeKinds.Booking &&
                            remainingQuantity === 0) ||
                          original?.kind === TradeKinds.OrderPatti
                    }
                    onClick={() => {
                      if (hasPendingAdvance) return showAdvancePendingError()
                      navigate(`${original.id}?new=true`)
                    }}
                  >
                    Add Hishob Patti
                  </MenuItem>
                  <MenuItem
                    disabled={
                      // Can add inward patti if
                      // 1. Trade is already a sauda patti
                      // 2. Trade status is verified
                      !(original?.advance_po
                        ? original?.kind === TradeKinds.SaudaPavti &&
                          original?.current_status_id === "Generate Trip"
                        : original?.kind === TradeKinds.SaudaPavti &&
                          original?.current_status_id === "Verified")
                    }
                    onClick={() => {
                      if (hasPendingAdvance) return showAdvancePendingError()
                      navigate(`${original.id}?newInward=true`)
                      return
                    }}
                  >
                    Add Inward Patti
                  </MenuItem>
                  <MenuItem
                    disabled={
                      !(
                        (original?.kind === TradeKinds.StoragePatti &&
                          original?.current_status_id === "Verified") ||
                        (original?.kind === TradeKinds.InwardPatti &&
                          original?.current_status_id ===
                            Codenames.GenerateInward)
                      )
                    }
                    onClick={() => {
                      if (hasPendingAdvance) return showAdvancePendingError()
                      navigate(`${original.id}?newBooking=true`)
                      return
                    }}
                  >
                    Add Transit Patti
                  </MenuItem>

                  <MenuItem
                    disabled={
                      // Can add storage patti if
                      // 1. Trade status is Generate Inward
                      !(
                        original?.kind === TradeKinds.InwardPatti &&
                        (original?.current_status_id ===
                          Codenames.GenerateInward ||
                          original?.current_status_id ===
                            Codenames.InwardConfirm)
                      )
                    }
                    onClick={() => {
                      if (hasPendingAdvance) return showAdvancePendingError()
                      navigate(`${original.id}?newStorage=true`)
                      return
                    }}
                  >
                    Add Storage Patti
                  </MenuItem>

                  {(original?.kind === TradeKinds.InwardPatti ||
                    original?.kind === TradeKinds.HishobPatti) && (
                    <>
                      <hr />
                      <div className="text-gray-800 font-600 p-2 underline">
                        Supplier Invoice Adjustment
                      </div>
                      <MenuItem
                        disabled={
                          (original?.kind === TradeKinds.InwardPatti &&
                            original?.current_status_id !==
                              Codenames.GenerateInward &&
                            original?.current_status_id !==
                              Codenames.InwardConfirm) ||
                          (original?.kind === TradeKinds.HishobPatti &&
                            original?.current_status_id !==
                              Codenames.GenerateSales &&
                            original?.current_status_id !== Codenames.Invoiced)
                        }
                        onClick={() => {
                          if (
                            tradeIssuanceNotesData?.trade_by_pk
                              ?.seller_credit_pending?.aggregate?.count
                          ) {
                            return openConfirmDialog({
                              isOpen: true,
                              message:
                                "Credit note in Pending state found! Please Approve/ Reject to add new.",
                              onSubmit: () => {
                                return
                              },
                              secondaryButtonProps: { hidden: true },
                            })
                          } else {
                            params.set("creditNote", "new")
                            params.set("tradeId", original.id)
                            params.set("user_role", "seller")
                            setParams(params)
                          }
                          return
                        }}
                      >
                        Add Credit Note
                      </MenuItem>
                      <MenuItem
                        disabled={
                          (original?.kind === TradeKinds.InwardPatti &&
                            original?.current_status_id !==
                              Codenames.GenerateInward &&
                            original?.current_status_id !==
                              Codenames.InwardConfirm) ||
                          (original?.kind === TradeKinds.HishobPatti &&
                            original?.current_status_id !==
                              Codenames.GenerateSales &&
                            original?.current_status_id !== Codenames.Invoiced)
                        }
                        onClick={() => {
                          if (
                            tradeIssuanceNotesData?.trade_by_pk
                              ?.seller_debit_pending?.aggregate?.count
                          ) {
                            return openConfirmDialog({
                              isOpen: true,
                              message:
                                "Debit note in Pending state found! Please Approve/ Reject to add new.",
                              onSubmit: () => {
                                return
                              },
                              secondaryButtonProps: { hidden: true },
                            })
                          } else {
                            params.set("debitNote", "new")
                            params.set("tradeId", original.id)
                            params.set("user_role", "seller")
                            setParams(params)
                          }

                          return
                        }}
                      >
                        Add Debit Note
                      </MenuItem>
                      <hr />
                      <MenuItem
                        disabled={
                          !Boolean(
                            tradeIssuanceNotesData?.trade_by_pk?.seller_credit
                              ?.aggregate?.count
                          )
                        }
                        onClick={() => {
                          params.set("creditNote", "view")
                          params.set("tradeId", original.id)
                          params.set("user_role", "seller")
                          setParams(params)

                          return
                        }}
                      >
                        View Credit Note
                        <Tag
                          intent="info"
                          variant="outline"
                          className="!rounded-xl ml-2"
                        >
                          {
                            tradeIssuanceNotesData?.trade_by_pk?.seller_credit
                              ?.aggregate?.count
                          }
                        </Tag>
                      </MenuItem>
                      <MenuItem
                        disabled={
                          !Boolean(
                            tradeIssuanceNotesData?.trade_by_pk?.seller_debit
                              ?.aggregate?.count
                          )
                        }
                        onClick={() => {
                          params.set("debitNote", "view")
                          params.set("tradeId", original.id)
                          params.set("user_role", "seller")
                          setParams(params)

                          return
                        }}
                      >
                        View Debit Note
                        <Tag
                          intent="info"
                          variant="outline"
                          className="!rounded-xl ml-2"
                        >
                          {
                            tradeIssuanceNotesData?.trade_by_pk?.seller_debit
                              ?.aggregate?.count
                          }
                        </Tag>
                      </MenuItem>
                    </>
                  )}
                  <hr />
                  {canShareViaWhatsApp && (
                    <>
                      <div className="text-gray-800 font-600 p-2 underline">
                        Share via WhatsApp
                      </div>
                      <MenuItem
                        onClick={() =>
                          shareViaWhatsApp({
                            hash: original?.buyer_hash,
                          })
                        }
                      >
                        Share to Buyer
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          shareViaWhatsApp({
                            hash: original?.seller_hash,
                          })
                        }
                      >
                        Share to Supplier
                      </MenuItem>
                      <hr />
                    </>
                  )}
                  <div className="text-gray-800 font-600 p-2 underline">
                    Other
                  </div>
                  <MenuItem
                    disabled={
                      !(
                        original?.kind === TradeKinds.SaudaPavti ||
                        original?.kind === TradeKinds.StoragePatti
                      )
                    }
                    onClick={() => {
                      let token = localStorage.getItem("token")
                      downloadPdf(
                        APIRoutes.downloadGatePass(
                          data?.trade_by_pk?.["seller_hash"] as string
                        ),
                        token
                      ).catch(err => {
                        return toast.custom(
                          <Toast
                            title={"Unable to download the receipt"}
                            intent="danger"
                          />
                        )
                      })
                    }}
                  >
                    Download GatePass
                  </MenuItem>
                  {/* <MenuItem
                    className="text-red"
                    hidden={!canDeleteTrades}
                    disabled={!isDraft}
                    // onClick={() => {
                    //   openDeleteTradesConfirm({
                    //     tradeIds: [original.id],
                    //     onSubmit: dumpTrade,
                    //   })
                    // }}
                  >
                    Delete Trade
                  </MenuItem> */}
                </Menu>
              }
            >
              <button className="hover:bg-gray-300 rounded">
                <DotsThreeVertical size={20} />
              </button>
            </Popover>
          )
        },
      },
    ]
  }, [selected, canShareViaWhatsApp, refetch, canDeleteTrades])

  const renderRowSubComponent = renderMultiCommoditySubRow(COLUMNS.length)

  const totalTrades = data?.trade_aggregate?.aggregate?.count || 0
  useEffect(() => {
    if (updatedAt) {
      refetch()
      params.delete("updatedAt")
      setParams(params)
    }
  }, [updatedAt])
  return (
    <>
      <header className="flex flex-col md:flex-row items-start md:items-center justify-between py-2 pl-2 pr-1 border-b-1 border-b-gray-300 gap-3">
        <SegmentedControl
          value={"trades"}
          onChange={value => {
            if (value === "receipts") {
              navigate(AppRoutes.tradeReceipts)
            } else if (value === "invoices" && canViewInvoice) {
              navigate(AppRoutes.tradeInvoices)
            } else if (value === "trip") {
              navigate(AppRoutes.tradeTrips)
            }
          }}
          className="w-full md:w-auto overflow-x-auto"
          data={[
            {
              label: "Trades",
              value: "trades",
              icon: <List className="mr-1" />,
            },
            {
              label: "Receipts",
              value: "receipts",
              icon: <Receipt className="mr-1" />,
            },
            canViewInvoice && {
              label: "Invoices",
              value: "invoices",
              icon: <OrdersCustom className="mr-1" />,
            },
            {
              label: "Trip",
              value: "trip",
              icon: <Truck className="mr-1" />,
            },
          ].filter(b => b)}
        />
        <div className="w-full md:flex-1 md:max-w-md flex items-center gap-3">
          <InputGroup
            className={
              "w-full !text-sm !leading-5 !px-3 !py-[6px] !h-8 md:!text-base md:!leading-6 md:!px-4 md:!py-2 md:!h-10"
            }
            inputProps={{
              value: search || "",
              placeholder: "Search by Pavti no., Vehicle no.",
              onChange(e) {
                params.set("search", e.target.value)
                params.delete("page")
                setParams(params)
              },
            }}
            leftElement={<MagnifyingGlass />}
          />
          <TradeRequestsFilter filter={filter} setFilter={setFilter} />
        </div>
        <div className="flex gap-3 justify-end w-full md:w-auto items-center">
          <div className="flex flex-col">
            {canAddMultiCommodity && (
              <Checkbox
                label="Is Multi Commodity"
                value={isMulti as any}
                checked={isMulti}
                onChange={() => setMulti(!isMulti)}
              />
            )}
          </div>
          <TradeActions />
          {!department && !currentDeptCodename ? null : (
            <Button
              className="text-sm leading-5 px-3 py-[6px] h-8 md:text-base md:leading-6 md:px-4 md:py-2 md:h-10"
              onClick={() =>
                navigate(
                  `/d/trade-requests/new?material_kind=${
                    isPackaging ? "packaging" : "commodity"
                  }`
                )
              }
              leftIcon={<Plus />}
            >
              Trade Request
            </Button>
          )}
        </div>
      </header>
      <div className="hidden md:block">
        <TableView
          relative={false}
          className="!h-[calc(100vh-64px-57px)]"
          renderRowSubComponent={renderRowSubComponent}
          paginationProps={{
            total: Math.ceil(totalTrades / siteConfig.pageSize),
            page: page,
            onChange(page) {
              params.set("page", page.toString())
              setParams(params)
            },
          }}
          isLoading={fetching}
          columns={COLUMNS}
          getRowProps={({ original }) => ({
            className:
              original?.seller?.user[0]?.is_blocked ||
              original?.buyer?.user[0]?.is_blocked
                ? "bg-red-100 hover:bg-red-100"
                : "bg-white",
          })}
          data={data?.trade || []}
          meta={<>{totalTrades} trade requests</>}
        />
      </div>
      <div className="md:hidden flex flex-col gap-3 p-3 overflow-y-auto !h-[calc(100vh-64px-57px)] pb-24">
        {fetching ? (
          <div className="text-center py-4 text-gray-500">Loading...</div>
        ) : !data?.trade || data.trade.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No Trades Found</div>
        ) : (
          <>
            {data.trade.map((trade: any) => (
              <TradeMobileCard
                key={trade.id}
                original={trade}
                columns={COLUMNS}
              />
            ))}

            {Math.ceil(totalTrades / siteConfig.pageSize) > 1 && (
              <div className="flex justify-between items-center py-4 px-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => {
                    params.set("page", (page - 1).toString())
                    setParams(params)
                  }}
                >
                  Previous
                </Button>
                <span className="text-sm font-500">
                  Page {page} of {Math.ceil(totalTrades / siteConfig.pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    page === Math.ceil(totalTrades / siteConfig.pageSize)
                  }
                  onClick={() => {
                    params.set("page", (page + 1).toString())
                    setParams(params)
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <UpdateTradeStatus
        bulkUpdateHook={useBulkUpdateTradeMutation}
        storeListHook={useTradeSelectorList}
      />
      <GenerateInvoices />
      <IssuanceNoteModal
        type="CREDIT"
        onSubmit={() => {
          refetch({
            requestPolicy: "network-only",
          })
        }}
      />
      <IssuanceNoteModal
        type="DEBIT"
        onSubmit={() => {
          refetch({
            requestPolicy: "network-only",
          })
        }}
      />
    </>
  )
}

export default AllTradeRequests
